import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { adminContentService, ManagedPressKitItem } from '@/lib/services/adminContentService';

const PressKitSection: React.FC = () => {
  const [pressKitItems, setPressKitItems] = useState<ManagedPressKitItem[]>([]);
  const [loading, setLoading] = useState(true);

  const brandAssets = [
    {
      title: 'Primary Logo',
      image: '/assets/images/LOGO-SIGLINE_PNG-1769633179089.png',
      alt: 'SIG Day primary logo with circular peace symbol in gold and brown colors on white background',
      format: 'PNG, SVG, EPS',
    },
    {
      title: 'Secondary Logo',
      image: '/assets/images/LOGO-SIGLINE_PNG-1769633179089.png',
      alt: 'SIG Day secondary logo with simplified text design in brown color scheme',
      format: 'PNG, SVG',
    },
    {
      title: 'Icon Set',
      image: '/assets/images/LOGO-SIGLINE_PNG-1769633179089.png',
      alt: 'Collection of SIG Day branded icons including peace symbols and heart shapes in gold',
      format: 'PNG, SVG',
    },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const data = await adminContentService.getPressKitItems();
      setPressKitItems(data.filter(pk => pk.is_visible));
    } catch (e) {
      console.warn('Failed to load press kit items');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section id="press-kit" className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="h-10 bg-muted rounded w-1/3 mx-auto" />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-48 bg-muted rounded" />)}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="press-kit" className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-6 py-3 rounded-full mb-6">
            <Icon name="FolderOpenIcon" size={24} className="text-primary" />
            <span className="text-primary font-cta text-sm font-bold">Press Kit Downloads</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-foreground mb-6">Media Resources & Assets</h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto font-body">
            Download comprehensive press materials, brand assets, and visual content for your coverage of SIG Day (The Global No Enemy Festival).
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {pressKitItems.map(item => (
            <div key={item.id} className="bg-card rounded-lg p-6 border border-border transition-all duration-300 hover:shadow-lg hover:border-primary group">
              <div className="flex items-start justify-between mb-4">
                <div className="p-3 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors duration-300">
                  <Icon name={item.icon as any} size={28} className="text-primary" />
                </div>
                <span className="text-xs font-cta font-bold text-muted-foreground bg-muted px-3 py-1 rounded-full">{item.category}</span>
              </div>
              <h3 className="text-xl font-headline font-bold text-foreground mb-2">{item.title}</h3>
              <p className="text-sm text-muted-foreground mb-4 font-body leading-relaxed">{item.description}</p>
              <div className="flex items-center justify-between pt-4 border-t border-border">
                <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                  <span className="font-body">{item.file_type}</span>
                  <span className="w-1 h-1 bg-muted-foreground rounded-full" />
                  <span className="font-body">{item.file_size}</span>
                </div>
                <a href={item.download_url !== '#' ? item.download_url : undefined} target="_blank" rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-primary font-cta text-sm font-bold transition-all duration-300 hover:text-peaceful-teal">
                  <Icon name="ArrowDownTrayIcon" size={18} />
                  <span>Download</span>
                </a>
              </div>
            </div>
          ))}
          {pressKitItems.length === 0 && (
            <div className="col-span-3 text-center py-16">
              <Icon name="FolderOpenIcon" size={64} className="text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-lg text-muted-foreground font-body">No press kit items available.</p>
            </div>
          )}
        </div>

        <div className="bg-card rounded-xl p-8 lg:p-12 border border-border">
          <h3 className="text-2xl lg:text-3xl font-headline font-bold text-foreground mb-8 text-center">Brand Asset Preview</h3>
          <div className="grid md:grid-cols-3 gap-8">
            {brandAssets.map((asset, index) => (
              <div key={index} className="text-center">
                <div className="bg-muted rounded-lg p-8 mb-4 overflow-hidden">
                  <AppImage src={asset.image} alt={asset.alt} className="w-full h-48 object-contain" />
                </div>
                <h4 className="text-lg font-headline font-bold text-foreground mb-2">{asset.title}</h4>
                <p className="text-sm text-muted-foreground font-body">{asset.format}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <button className="px-8 py-4 bg-primary text-primary-foreground rounded-lg font-cta text-base font-bold transition-all duration-300 hover:scale-105 hover:shadow-lg inline-flex items-center space-x-2">
              <Icon name="ArrowDownTrayIcon" size={20} />
              <span>Download Complete Brand Package</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PressKitSection;